import React, { useState, useEffect, useMemo } from 'react';
import { Card, Button, Spin, Space, Modal, message, Popconfirm } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, gql, useLazyQuery, useMutation } from '@apollo/client';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import DataGrid from '../../components/DataGrid';
import * as Config from '../../appConfig';
import { activePeriod, disabledDate } from '../../utils/date';

import {
  updateTableMetaData,
  updateGridMetaData,
  resetGridFilters,
} from '../../store/reducers/expenseSlice';
import { LEAVE_LIST_FIELDS } from '../../apollo/fragments';
import {
  EMPLOYEE_LOOKUP_QUERY,
  LEAVE_TYPE_OPTIONS_QUERY,
} from '../../apollo/queries';
import {
  ADD_LEAVE_MUTATION,
  UPDATE_LEAVE_MUTATION,
  DELETE_LEAVE_MUTATION,
} from '../../apollo/mutations';

import AddLeaveForm from '../../components/Expense/Forms/AddLeaveForm';
import ProfileCard from '../../components/Employee/ProfileCard';

const LEAVES_QUERY = gql`
  ${LEAVE_LIST_FIELDS}
  query Leaves(
    $first: Int
    $page: Int
    $filters: [QueryGetLeavesFilter]
    $sort: QueryGetLeavesSorter
  ) {
    leaves(first: $first, page: $page, filters: $filters, sort: $sort) {
      paginatorInfo {
        count
        currentPage
        total
      }
      data {
        ...LeaveListFields
      }
    }
  }
`;

const Leaves = ({ gridId }) => {
  const { metaData, grids } = useSelector((state) => state.expense);
  const {
    user: { ability },
  } = useSelector((state) => state.login);
  const dispatch = useDispatch();

  const grid = grids.find((i) => i.id === gridId);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showAddEditLeaveModal, setShowAddEditLeaveModal] = useState(false);
  const [activeLeave, setActiveLeave] = useState(null);

  const [freezePrevMonth, currentMonth, prevMonth] = activePeriod();

  const queryVariables = useMemo(() => {
    return {
      first: grid.metaData.pagination.pageSize,
      page: grid.metaData.pagination.page,
      filters: grid.metaData.filters,
      sort: grid.metaData.sorting,
    };
  }, [
    grid.metaData.pagination.pageSize,
    grid.metaData.pagination.page,
    grid.metaData.filters,
    grid.metaData.sorting,
  ]);

  const { loading, data, error, refetch } = useQuery(LEAVES_QUERY, {
    variables: queryVariables,
  });

  console.log('ERROR', error);

  const [
    lookupEmployee,
    { data: lookupData, loading: fetching },
  ] = useLazyQuery(EMPLOYEE_LOOKUP_QUERY);

  const { loading: loadingTypes, data: types } = useQuery(
    LEAVE_TYPE_OPTIONS_QUERY
  );

  const [addLeave, { loading: adding }] = useMutation(ADD_LEAVE_MUTATION);
  const [updateLeave, { loading: updating }] = useMutation(
    UPDATE_LEAVE_MUTATION
  );
  const [deleteLeave, { loading: deleting }] = useMutation(
    DELETE_LEAVE_MUTATION
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const onEmployeeLookup = (search) => {
    lookupEmployee({
      variables: {
        search,
        status: 'active',
      },
    });
  };

  const onSuccessMutation = (mesageText = 'Details updated successfully!') => {
    refetch(queryVariables);
    message.success(mesageText);
  };

  const PartialDays = {
    0: 'No Half Day',
    1: 'Start Day',
    2: 'End Day',
  };

  const columns = [
    {
      title: 'Sl.No',
      dataIndex: 'id',
      key: 'id',
      serverKey: 'ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      className: 'width-100',
    },
    {
      title: 'EMP ID',
      dataIndex: ['employee', 'emp_id'],
      key: 'emp_id',
      serverKey: 'EMP_ID',
      sorter: true,
      canExclude: false,
      visible: true,
      dataType: 'string',
      render: (value, record) => {
        return <ProfileCard employee={record.employee} />;
      },
    },
    {
      title: 'Name',
      dataIndex: ['employee', 'first_name'],
      key: 'first_name',
      serverKey: 'NAME',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return (
          record && `${record.employee.first_name} ${record.employee.last_name}`
        );
      },
    },
    {
      title: 'Type',
      dataIndex: ['leave_type', 'name'],
      key: 'leave_type',
      serverKey: 'TYPE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Date From',
      dataIndex: 'date_from',
      key: 'date_from',
      serverKey: 'DATE_FROM',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Date To',
      dataIndex: 'date_to',
      key: 'date_to',
      serverKey: 'DATE_TO',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Half Day',
      dataIndex: 'half_day',
      key: 'half_day',
      serverKey: 'HALF_DAY',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
      render: (value, record) => {
        return PartialDays[value] ? PartialDays[value] : value;
      },
    },
    {
      title: 'No. Of Days',
      dataIndex: 'days_calculated',
      key: 'days_calculated',
      serverKey: 'DAYS',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      serverKey: 'NOTE',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: true,
      dataType: 'string',
    },
    {
      title: 'Created By',
      dataIndex: ['createdBy', 'name'],
      key: 'created_by',
      serverKey: 'USER',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'string',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      serverKey: 'CREATED_AT',
      sorter: true,
      canExclude: false,
      visible: true,
      ellipsis: false,
      dataType: 'date',
      format: 'date',
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      serverKey: 'ACTIONS',
      sorter: false,
      canExclude: false,
      visible: true,
      ellipsis: false,
      disableSearch: true,
      className: 'width-75',
      render: (value, record) => {
        const date_from = moment(record.date_from);
        const date_to = moment(record.date_to);
        const isEditable =
          disabledDate(date_from, freezePrevMonth, currentMonth, prevMonth) ||
          disabledDate(date_to, freezePrevMonth, currentMonth, prevMonth);
        return (
          <Space>
            {ability.toEditLeave && (
              <Button
                type="default"
                size="small"
                icon={<EditOutlined />}
                title="Edit"
                onClick={() => {
                  setActiveLeave(record);
                  setShowAddEditLeaveModal(true);
                }}
                disabled={isEditable}
              ></Button>
            )}
            {ability.toDeleteLeave && (
              <Popconfirm
                title="Are you sure to delete this?"
                onConfirm={() => {
                  onDeleteConfirm(record);
                }}
                onCancel={() => {}}
                okText="Delete"
                cancelText="No"
                placement="rightTop"
                disabled={isEditable}
              >
                <Button
                  type="ghost"
                  size="small"
                  icon={<DeleteOutlined />}
                  title="Delete"
                  danger
                  disabled={isEditable}
                ></Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // const columnsForExport = [...columns];

  useEffect(() => {
    if (grid) {
      console.log('refetch for metaData with///', queryVariables);
      refetch(queryVariables);
    }
  }, [dispatch, refetch, grid, queryVariables]);

  const onChange = (updatedMetaData) => {
    console.log('onChange///', updatedMetaData);
    dispatch(
      updateTableMetaData({
        metaData: {
          ...metaData,
          ...updatedMetaData,
        },
      })
    );
    dispatch(
      updateGridMetaData({
        gridId,
        metaData: updatedMetaData,
      })
    );
  };

  const onLeaveSubmit = (values, callBack) => {
    console.log('onLeaveSubmit called with///', values);
    const isUpdate = values.id && values.id !== null;
    const variables = {
      id: isUpdate ? parseInt(values.id, 10) : values.employee.value,
      input: {
        leave_type_id: parseInt(values.leave_type_id, 10),
        date_from:
          values.leave_period && values.leave_period && values.leave_period[0]
            ? values.leave_period[0].format(Config.DATE_FORMAT_SERVER)
            : null,
        date_to:
          values.leave_period && values.leave_period && values.leave_period[1]
            ? values.leave_period[1].format(Config.DATE_FORMAT_SERVER)
            : null,
        half_day: parseInt(values.half_day, 10),
        days_calculated: parseFloat(values.days_calculated),
        note: values.note ? values.note : '',
      },
    };
    const mutationToCall = isUpdate ? updateLeave : addLeave;
    mutationToCall({
      variables,
    })
      .then(({ data }) => {
        const leave = isUpdate ? data.updateLeave : data.addLeave;
        console.log('onLeaveSubmit finished', leave);
        if (leave) {
          callBack({ status: true });
          onSuccessMutation();
          setShowAddEditLeaveModal(false);
        }
        setActiveLeave(null);
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
        callBack({ status: false });
      });
  };

  const onDeleteConfirm = (leave) => {
    console.log('onDeleteConfirm called with///', leave);
    deleteLeave({
      variables: {
        id: leave.id,
      },
    })
      .then(({ data: { deleteLeave } }) => {
        console.log('onDeleteConfirm finished', deleteLeave);
        if (deleteLeave.id) {
          onSuccessMutation('Leave deleted!');
        }
      })
      .catch((e) => {
        console.log('mutationError ////', e, e.graphQLErrors);
        let error = 'Some error occured, Please try again !!!';
        message.error(error);
      });
  };

  const onAddEditModalClose = () => {
    setShowAddEditLeaveModal(false);
    setActiveLeave(null);
  };

  console.log('fetching ///2', fetching);

  return (
    <Spin spinning={loading || loadingTypes || adding || updating || deleting}>
      <Card className="employeesCard" style={{ minHeight: '200px' }}>
        {/* {error && <Error message={error.message} />} */}

        {data && (
          <DataGrid
            dataSource={data.leaves.data}
            columns={columns}
            rowSelection={rowSelection}
            filters={grid.metaData.filters}
            visibleColumns={grid.metaData.visibleColumns}
            sorting={grid.metaData.sorting}
            pagination={grid.metaData.pagination}
            onChange={onChange}
            totalRecords={data.leaves.paginatorInfo.total}
            onClearFilter={() => {
              dispatch(resetGridFilters({ gridId }));
            }}
            onAddNewEntry={
              ability.toDeleteLeave &&
              (() => {
                setShowAddEditLeaveModal(true);
              })
            }
            addNewEntryBtnLabel="Add Leave"
          />
        )}
      </Card>
      <Modal
        title={activeLeave ? 'Edit Leave' : 'Add Leave'}
        visible={showAddEditLeaveModal}
        confirmLoading={false}
        destroyOnClose={true}
        centered
        style={{ maxWidth: '700px' }}
        width="80%"
        footer={null}
        onCancel={onAddEditModalClose}
        maskClosable={false}
      >
        <Spin spinning={adding || updating}>
          <AddLeaveForm
            leave={activeLeave}
            lookupData={
              lookupData && lookupData.employeeLookup
                ? lookupData.employeeLookup.map((i) => {
                    return {
                      label: `${i.first_name} ${i.last_name} (${i.emp_id})`,
                      value: i.id,
                    };
                  })
                : []
            }
            onLookup={onEmployeeLookup}
            types={types ? types.leaveTypes : []}
            onLeaveSubmit={onLeaveSubmit}
            onCancelClick={onAddEditModalClose}
            fetching={fetching}
            ability={ability}
          />
        </Spin>
      </Modal>
    </Spin>
  );
};

export default Leaves;
